import React, { useState, useEffect } from "react";
import { ArrowRight, Star, ChevronDown, ChevronUp, Check } from "lucide-react";
import { ShieldCheck } from "@phosphor-icons/react";
import ContactForm from "./ContactForm";
import ContactModal from "./ContactModal";
import PricingCard from "./PricingCard";
import PricingTabsSection from "./PricingTabsSection";
import MobileSlider from "./MobileSlider";
import SEO from "./SEO";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

const ServicePageLayout = ({ data }) => {
  const [activeFaq, setActiveFaq] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [emblaRef] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 5000, stopOnInteraction: false }),
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const {
    banner,
    whyNeed,
    howWeWork,
    pricing,
    whyChooseUs,
    cta,
    testimonials,
    faqs,
  } = data;

  return (
    <div className="min-h-screen font-body overflow-x-hidden">
      <SEO
        title={`${(banner.headingStart || "").replace(/<[^>]*>/g, "")} ${(banner.headingHighlight || "").replace(/<[^>]*>/g, "")}`.trim()}
        description={
          banner.paragraph ||
          `Professional IP services starting at $45. Trademark Facility — trusted protection across the USA.`
        }
        keywords={`${(banner.headingStart || "").replace(/<[^>]*>/g, "")}, intellectual property, USPTO, legal services`}
      />

      {/* ─── BANNER ─── */}
      <section
        className="relative bg-primary overflow-hidden"
        data-testid="banner-section"
      >
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-10 md:py-24 relative z-10">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-20 items-start overflow-hidden">
            <div className="lg:hidden w-full max-w-full overflow-hidden min-w-0">
              <ContactForm title={banner.formTitle} buttonText="Get Started" />
            </div>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 border border-lime/30 bg-lime-light/10 rounded-full px-3 py-1 sm:px-4 sm:py-1.5 mb-6">
                <span className="w-2 h-2 rounded-full bg-lime-400 animate-pulse" />
                <span className="text-[10px] sm:text-sm font-medium text-lime-300 tracking-wide">
                  {banner.upperLine}
                </span>
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter leading-tight mb-5">
                {banner.headingStart}
                <span className="text-lime"> {banner.headingHighlight}</span>
              </h1>
              <p className="text-sm sm:text-lg text-gray-400 leading-relaxed max-w-xl mb-6">
                {banner.paragraph}
              </p>
              {banner.badges && (
                <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs sm:text-sm">
                  {banner.badges.map((badge, i) => (
                    <div key={i} className="flex items-center text-gray-300">
                      <Check className="h-4 w-4 text-lime mr-2 shrink-0" />
                      <span>{badge}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="hidden lg:block">
              <ContactForm title={banner.formTitle} buttonText="Get Started" />
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHY YOU NEED — image first on mobile ─── */}
      <section
        className="bg-white py-14 md:py-28"
        data-testid="why-need-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-stretch overflow-hidden">
            <div className="flex">
              <img
                src={whyNeed.image}
                alt={whyNeed.heading}
                className="w-full h-[250px] md:h-auto object-cover rounded-2xl"
              />
            </div>
            <div className="flex flex-col justify-center">
              <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
                Why you need this
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold  tracking-tight mt-2 mb-4 leading-tight">
                {whyNeed.heading}
              </h2>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base mb-6">
                {whyNeed.paragraph}
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="group inline-flex items-center bg-lime hover:bg-lime-light text-white font-bold py-2.5 px-5 rounded-xl transition-all duration-300 active:scale-95 text-sm self-start"
              >
                Get Started Now{" "}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── HOW WE WORK ─── */}
      {/* <section
        className="bg-gray-50 py-14 md:py-28"
        data-testid="how-we-work-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="mb-14">
            <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
              Our process
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold  tracking-tight mt-3">
              {howWeWork.heading}
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl">
              {howWeWork.paragraph}
            </p>
          </div>
          <MobileSlider enabled={isMobile}>
            {howWeWork.steps.map((step, i) => (
              <div
                key={i}
                className="group relative bg-white border border-gray-200 rounded-xl p-8 h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-lime/40"
              >
                <span className="font-heading text-6xl font-extrabold text-gray-100 group-hover:text-lime-50 absolute top-4 right-6 transition-colors duration-300">
                  {step.number}
                </span>
                <div className="relative z-10">
                  <h3 className="font-heading text-xl font-bold  mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </MobileSlider>
        </div>
      </section> */}

      {/* ─── PRICING ─── */}
      {data.pricingTabs ? (
        <PricingTabsSection onCtaClick={() => setIsModalOpen(true)} />
      ) : (
        <section
          className="bg-white py-14 md:py-28"
          data-testid="pricing-section"
        >
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-14">
              <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
                Pricing
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold  tracking-tight mt-3">
                {pricing.heading}
              </h2>
              <p className="text-gray-600 mt-3 max-w-xl mx-auto">
                {pricing.paragraph}
              </p>
            </div>
            <div
              className={`grid grid-cols-1 md:grid-cols-2 ${pricing.packages.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-6`}
            >
              {pricing.packages.map((pkg, i) => (
                <PricingCard
                  key={i}
                  {...pkg}
                  onCtaClick={() => setIsModalOpen(true)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── WHY CHOOSE US — image first on mobile ─── */}
      <section
        className="bg-primary py-14 md:py-28"
        data-testid="why-choose-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-stretch overflow-hidden">
            <div className="order-2 md:order-1 flex flex-col justify-center">
              <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
                Why choose us
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight mt-2 mb-4">
                {whyChooseUs.heading}
              </h2>
              <p className="text-gray-400 leading-relaxed text-sm sm:text-base mb-6">
                {whyChooseUs.paragraph}{" "}
                {whyChooseUs.usps.map((u) => u.description).join(" ")}
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="group inline-flex items-center bg-lime hover:bg-lime-light  font-bold py-2.5 px-5 rounded-xl transition-all duration-300 active:scale-95 text-sm self-start"
              >
                Get Started Today{" "}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
            <div className="order-1 md:order-2 flex">
              <img
                src="https://images.unsplash.com/photo-1664574654578-d5a6a4f447bb?crop=entropy&cs=srgb&fm=jpg&w=800&h=650&fit=crop"
                alt="Legal professionals at work"
                className="w-full h-[250px] md:h-auto object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="bg-primary py-14 md:py-28" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-6">
            {cta.heading}
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
            {cta.paragraph}
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            data-testid="cta-button"
            className="group inline-flex items-center justify-center bg-lime  hover:bg-lime-light font-bold py-2.5 px-6 rounded-xl transition-all duration-300 hover:shadow-xl active:scale-95"
          >
            {cta.buttonText}
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
          </button>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section
        className="bg-gray-50 py-14 md:py-28"
        data-testid="testimonials-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="mb-14">
            <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
              Testimonials
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#2d3132] tracking-tight mt-3">
              {testimonials.heading || "What our clients say"}
            </h2>
          </div>
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {testimonials.items.map((t, i) => (
                <div
                  key={i}
                  className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.33%] min-w-0 pl-4"
                >
                  <div className="bg-white border border-gray-200 rounded-xl p-8 h-full mr-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-lime/40">
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(5)].map((_, j) => (
                        <Star
                          key={j}
                          className="h-4 w-4 text-amber-400 fill-amber-400"
                        />
                      ))}
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-6">
                      "{t.text}"
                    </p>
                    <div className="flex items-center gap-3">
                      <img
                        src={t.image}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-bold text-sm">{t.name}</div>
                        <div className="text-gray-500 text-xs">{t.role}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQS ─── */}
      <section className="bg-white py-14 md:py-28" data-testid="faqs-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="mb-14">
            <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
              FAQ
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight mt-3">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="divide-y divide-gray-200">
            {faqs.map((faq, i) => (
              <div key={i} data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  className="w-full flex justify-between items-center py-5 text-left group"
                >
                  <span className="font-heading font-bold pr-4 group-hover:text-lime transition-colors duration-200">
                    {faq.question}
                  </span>
                  {activeFaq === i ? (
                    <ChevronUp className="h-5 w-5 text-lime shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400 shrink-0" />
                  )}
                </button>
                {activeFaq === i && (
                  <div className="pb-5 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={banner.formTitle}
      />
    </div>
  );
};

export default ServicePageLayout;
